import type { ComponentProps } from 'react';

import { HoverCard } from '@components/ui/hover-card';
import { Label } from '@components/ui/label';
import { formatCentsToEUR } from '@formatters/currency.formatter';
import { formatDate } from '@formatters/date.formatter';
import { cn } from '@lib/utils';

type DetailItemProps = {
  canBeHovered?: boolean;
  className?: string;
  isFullLine?: boolean;
  label: string;
  type?: 'date' | 'money' | 'default';
  value?: number | string;
};

export function DetailItem({
  canBeHovered = false,
  className,
  isFullLine,
  label,
  type = 'default',
  value,
}: DetailItemProps) {
  const formatterByType = getFormatterByType[type];

  const formattedValue = formatterByType(value);

  return (
    <div
      className={cn('flex flex-col gap-px', {
        'col-span-2': isFullLine,
      })}
    >
      <Label className="text-sm font-normal text-gray-400 dark:text-zinc-400">
        {label}
      </Label>
      {canBeHovered ? (
        <HoverCard.Root openDelay={200}>
          <HoverCard.Trigger asChild>
            <Value className={className} isClamped value={formattedValue} />
          </HoverCard.Trigger>
          <HoverCard.Content design="email" side="top">
            <Value className={className} value={formattedValue} />
          </HoverCard.Content>
        </HoverCard.Root>
      ) : (
        <Value className={className} value={formattedValue} />
      )}
    </div>
  );
}

type ValueProps = ComponentProps<'span'> & {
  isClamped?: boolean;
  value: number | string;
};

function Value({ className, isClamped = false, value, ...props }: ValueProps) {
  return (
    <span
      className={cn(
        'text-sm leading-tight font-semibold text-gray-500 dark:text-gray-300',
        className,
        {
          'line-clamp-3': isClamped,
          hidden: !value,
        },
      )}
      {...props}
    >
      {value}
    </span>
  );
}

const getFormatterByType: Record<
  string,
  (value?: number | string) => number | string
> = {
  date: value => formatDate(value, { format: 'DD/MM/YYYY' }),
  money: value => formatCentsToEUR(Number(value)),
  default: value => value || '',
};
